import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Lock, BookMarked, Loader2, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import useMeta from "@/hooks/useMeta";

export default function ClubsPrivateLanding() {
    const { t } = useTranslation();
    useMeta({
        title: "Clubes de lectura privados · RecomendaLibros",
        description: "Crea un club de lectura privado con tus amigos. Guías de discusión generadas con IA, lectura colaborativa, sin distracciones de redes sociales.",
        canonical: "/clubs-private",
    });
    const [form, setForm] = useState({ name: "", email: "", wishlist: "" });
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/clubs/interest", {
                name: form.name.trim(),
                email: form.email.trim().toLowerCase(),
                wishlist: form.wishlist.trim(),
            });
            setSent(true);
            toast.success(t("clubs_private.submitted_toast"));
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <main
            data-testid="clubs-private-landing"
            className="min-h-[calc(100vh-73px)] max-w-3xl mx-auto px-5 md:px-10 py-16 md:py-24"
        >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[var(--border-strong)] text-xs uppercase tracking-widest text-[var(--accent)] mb-6">
                <Lock size={12} strokeWidth={1.5} />
                {t("clubs_private.eyebrow")}
            </div>
            <h1
                className="font-display text-4xl md:text-6xl text-[var(--ink)] tracking-tight mb-5"
                style={{ fontVariationSettings: '"wght" 700, "wdth" 78, "opsz" 144' }}
            >
                {t("clubs_private.title")}
            </h1>
            <p className="text-[var(--ink-muted)] text-lg leading-relaxed mb-4 max-w-2xl">
                {t("clubs_private.lead")}
            </p>
            <p className="text-[var(--ink-dim)] text-sm leading-relaxed mb-12 max-w-2xl">
                {t("clubs_private.sublead")}
            </p>

            {sent ? (
                <div
                    data-testid="clubs-private-thanks"
                    className="border border-[var(--accent)] bg-[var(--bg-elevated)] p-8 rounded-md max-w-xl"
                >
                    <CheckCircle2 size={28} strokeWidth={1.5} className="text-[var(--accent)] mb-3" />
                    <h2
                        className="font-display text-2xl text-[var(--ink)] mb-3"
                        style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 32' }}
                    >
                        {t("clubs_private.thanks_title")}
                    </h2>
                    <p className="text-[var(--ink-muted)] text-sm leading-relaxed mb-6">
                        {t("clubs_private.thanks_lead")}
                    </p>
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-sm uppercase tracking-widest text-[var(--ink-muted)] hover:text-[var(--accent)]"
                        data-testid="clubs-private-back-home"
                    >
                        <BookMarked size={14} strokeWidth={1.5} />
                        {t("clubs_private.back_home")}
                    </Link>
                </div>
            ) : (
                <form
                    onSubmit={submit}
                    data-testid="clubs-private-form"
                    className="space-y-6 max-w-xl"
                >
                    <Field label={t("clubs_private.field_name")}>
                        <input
                            data-testid="clubs-private-name"
                            type="text"
                            required
                            minLength={2}
                            maxLength={80}
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className={inputCls}
                        />
                    </Field>
                    <Field label={t("clubs_private.field_email")}>
                        <input
                            data-testid="clubs-private-email"
                            type="email"
                            required
                            maxLength={200}
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            className={inputCls}
                        />
                    </Field>
                    <Field
                        label={t("clubs_private.field_wishlist")}
                        hint={t("clubs_private.field_wishlist_hint")}
                    >
                        <textarea
                            data-testid="clubs-private-wishlist"
                            rows={5}
                            maxLength={1200}
                            value={form.wishlist}
                            onChange={(e) => setForm({ ...form, wishlist: e.target.value })}
                            className={`${inputCls} resize-none`}
                            placeholder={t("clubs_private.field_wishlist_placeholder")}
                        />
                    </Field>
                    <button
                        type="submit"
                        data-testid="clubs-private-submit"
                        disabled={busy}
                        className="inline-flex items-center gap-2 bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] disabled:opacity-50 transition-colors px-8 py-3 text-sm uppercase tracking-widest"
                    >
                        {busy && <Loader2 size={14} className="animate-spin" />}
                        {t("clubs_private.submit")}
                    </button>
                    <p className="text-xs text-[var(--ink-dim)] leading-relaxed">
                        {t("clubs_private.privacy_note")}
                    </p>
                </form>
            )}
        </main>
    );
}

const inputCls =
    "w-full bg-transparent border-b border-[var(--ink)] py-2 text-base text-[var(--ink)] focus:outline-none focus:border-[var(--accent)] transition-colors";

function Field({ label, hint, children }) {
    return (
        <label className="block">
            <span className="block text-xs uppercase tracking-widest text-[var(--ink-muted)] mb-2">
                {label}
            </span>
            {children}
            {hint && <p className="text-xs text-[var(--ink-dim)] mt-1.5">{hint}</p>}
        </label>
    );
}
